import { FC } from "react";
import { cn } from "@/lib/utils";
import { ProgramWeek } from "@entities/program/types/base";
import {
  getProgramDayDate,
  getProgramDayDateParts,
} from "@entities/program/lib/getWeekDates";

interface WeekCalendarProps {
  week: ProgramWeek;
  selectedDayValue: string;
  onDaySelect: (dayValue: string, day: ProgramWeek["days"][number]) => void;
}

const getDateKey = (date: Date | null): string | null => {
  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDayTone = (
  day: ProgramWeek["days"][number],
  isSelected: boolean,
  isToday: boolean,
) => {
  if (day.status === "skipped") {
    return cn(
      "bg-[#E1E2DD] text-[#666A63]",
      isSelected && "ring-2 ring-[#DDFB1F] shadow-[0_10px_22px_rgba(80,84,78,0.14)]",
      isToday && !isSelected && "ring-2 ring-[#DDFB1F]/70",
    );
  }

  if (day.status === "completed") {
    return cn(
      "bg-[#7F86D3] text-white",
      isSelected && "ring-2 ring-[#DDFB1F] shadow-[0_10px_22px_rgba(127,134,211,0.2)]",
      isToday && !isSelected && "ring-2 ring-[#DDFB1F]/70",
    );
  }

  if (isSelected) {
    return "bg-[#DDFB1F] text-[#111111] shadow-[0_10px_24px_rgba(221,251,31,0.24)]";
  }

  if (day.workout_id) {
    return cn(
      "bg-[#EEE7FF] text-[#111111]",
      isToday &&
        "ring-2 ring-[#DDFB1F]/70 shadow-[0_8px_20px_rgba(221,251,31,0.18)]",
    );
  }

  if (day.overdue) {
    return cn(
      "bg-[#FFF0F0] text-[#F05F61]",
      isToday &&
        "ring-2 ring-[#DDFB1F]/70 shadow-[0_8px_20px_rgba(221,251,31,0.18)]",
    );
  }

  return cn(
    "bg-[#F7F7F3] text-[#111111]",
    isToday &&
      "ring-2 ring-[#DDFB1F]/70 shadow-[0_8px_20px_rgba(221,251,31,0.18)]",
  );
};

const isPresent = <T,>(value: T | null | undefined): value is T =>
  value != null;

export const WeekCalendar: FC<WeekCalendarProps> = ({
  onDaySelect,
  selectedDayValue,
  week,
}) => {
  const todayDateKey = getDateKey(new Date());

  return (
    <div className="relative z-10 -mb-8 -mt-2 overflow-x-auto px-5 pb-9 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <div className="flex w-full min-w-max justify-between gap-3">
        {week.days.filter(isPresent).map((day) => {
          const calendarDate = getProgramDayDate({
            calendarDate: day.calendar_date,
            dayNum: day.day_num,
            dueAt: day.due_at,
            weekStartAt: week.week_start_at,
          });
          const { day: dayNumber, month } = getProgramDayDateParts(
            calendarDate,
          );
          const isToday =
            todayDateKey !== null && getDateKey(calendarDate) === todayDateKey;
          const isSelected = selectedDayValue === day.day_num.toString();

          return (
            <button
              aria-current={isToday ? "date" : undefined}
              key={day.day_num}
              type="button"
              onClick={() => onDaySelect(day.day_num.toString(), day)}
              className={cn(
                "relative flex h-[78px] min-w-[48px] flex-col items-center justify-center rounded-[23px] transition-[transform,background-color] duration-200 ease-out active:scale-[0.97]",
                getDayTone(day, isSelected, isToday),
              )}
            >
              <span className="text-[22px] font-black leading-none">
                {day.day_num}
              </span>
              <span className="mt-1.5 text-[12px] font-medium leading-none">
                {dayNumber}
              </span>
              <span className="mt-1 text-[12px] font-medium leading-none">
                {month}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
